/**
 * @quotech/ui - Universal Password Settings Tab
 *
 * PATH: packages/ui/src/components/settings/PasswordTab.tsx
 * PURPOSE: Password change functionality across all hubs (orchestrator)
 * WHY: Central settings component used by every hub's settings page
 *
 * FLOW:
 * ┌───────────────┐    ┌──────────────────┐    ┌──────────────────────┐
 * │ user inputs   │ →  │ validate + submit │ →  │ onChangePassword()   │
 * └───────────────┘    └──────────────────┘    └──────────────────────┘
 *
 * DEPENDENCIES:
 *  - PasswordStrengthIndicator: visual strength bar + checklist
 *  - password-tab-types: shared type definitions
 */

import { useState, useCallback } from 'react'
import { Lock, Eye, EyeOff, Check, X, Loader2, AlertCircle, ShieldCheck } from 'lucide-react'
import { cn } from '../../lib/utils'
import { PasswordStrengthIndicator } from './password-tab/PasswordStrengthIndicator'
import type { PasswordTabProps, PasswordInputProps } from './password-tab/password-tab-types'

// Re-export types for consumers
export type { PasswordTabProps } from './password-tab/password-tab-types'

// =============================================================================
// PASSWORD INPUT COMPONENT
// =============================================================================

function PasswordInput({ label, value, onChange, placeholder, disabled, error }: PasswordInputProps) {
  const [showPassword, setShowPassword] = useState(false)

  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
        {label}
      </label>
      <div className="relative">
        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          type={showPassword ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            'w-full pl-10 pr-10 py-2 rounded-lg text-sm transition-colors',
            'bg-white dark:bg-slate-800 border',
            error
              ? 'border-red-500 focus:ring-red-500/50'
              : 'border-slate-200 dark:border-slate-700 focus:ring-teal-500/50 focus:border-teal-500',
            'text-slate-900 dark:text-white placeholder-slate-400',
            'focus:outline-none focus:ring-2',
            disabled && 'opacity-60 cursor-not-allowed bg-slate-50 dark:bg-slate-900'
          )}
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
        >
          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
      {error && (
        <p className="text-xs text-red-500 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          {error}
        </p>
      )}
    </div>
  )
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function PasswordTab({
  onChangePassword,
  isLoading = false,
  minLength = 8,
  requireSpecialChar = true,
  requireNumber = true,
  requireUppercase = true,
  className,
}: PasswordTabProps) {
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [success, setSuccess] = useState(false)

  const validatePassword = useCallback((password: string): boolean => {
    if (password.length < minLength) return false
    if (requireUppercase && !/[A-Z]/.test(password)) return false
    if (requireNumber && !/\d/.test(password)) return false
    if (requireSpecialChar && !/[!@#$%^&*(),.?":{}|<>]/.test(password)) return false
    return true
  }, [minLength, requireUppercase, requireNumber, requireSpecialChar])

  const handleSubmit = useCallback(async () => {
    if (!onChangePassword) return

    setErrors({})
    setSuccess(false)

    const newErrors: Record<string, string> = {}

    if (!currentPassword) {
      newErrors.currentPassword = 'Current password is required'
    }

    if (!newPassword) {
      newErrors.newPassword = 'New password is required'
    } else if (!validatePassword(newPassword)) {
      newErrors.newPassword = 'Password does not meet requirements'
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your new password'
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    try {
      await onChangePassword(currentPassword, newPassword)
      setSuccess(true)
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (err) {
      setErrors({
        currentPassword: err instanceof Error ? err.message : 'Failed to change password'
      })
    }
  }, [currentPassword, newPassword, confirmPassword, validatePassword, onChangePassword])

  const handleReset = useCallback(() => {
    setCurrentPassword('')
    setNewPassword('')
    setConfirmPassword('')
    setErrors({})
    setSuccess(false)
  }, [])

  return (
    <div className={cn('space-y-6', className)}>
      {/* Success message */}
      {success && (
        <div className="flex items-center gap-3 p-4 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
          <ShieldCheck className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
          <div>
            <p className="font-medium text-green-800 dark:text-green-200">
              Password changed successfully
            </p>
            <p className="text-sm text-green-600 dark:text-green-400">
              Your password has been updated. Use your new password next time you sign in.
            </p>
          </div>
        </div>
      )}

      {/* Form */}
      <div className="space-y-4">
        <PasswordInput
          label="Current Password"
          value={currentPassword}
          onChange={setCurrentPassword}
          placeholder="Enter your current password"
          disabled={isLoading}
          error={errors.currentPassword}
        />

        <PasswordInput
          label="New Password"
          value={newPassword}
          onChange={setNewPassword}
          placeholder="Enter your new password"
          disabled={isLoading}
          error={errors.newPassword}
        />

        {/* Strength indicator */}
        {newPassword && (
          <PasswordStrengthIndicator
            password={newPassword}
            minLength={minLength}
            requireSpecialChar={requireSpecialChar}
            requireNumber={requireNumber}
            requireUppercase={requireUppercase}
          />
        )}

        <PasswordInput
          label="Confirm New Password"
          value={confirmPassword}
          onChange={setConfirmPassword}
          placeholder="Confirm your new password"
          disabled={isLoading}
          error={errors.confirmPassword}
        />
      </div>

      {/* Actions */}
      {onChangePassword && (
        <div className="flex items-center gap-3 pt-4 border-t border-slate-200 dark:border-slate-800">
          <button
            onClick={handleSubmit}
            disabled={isLoading || !currentPassword || !newPassword || !confirmPassword}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              'bg-teal-500 text-white hover:bg-teal-600',
              (isLoading || !currentPassword || !newPassword || !confirmPassword) && 'opacity-60 cursor-not-allowed'
            )}
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Check className="w-4 h-4" />
            )}
            Change Password
          </button>

          <button
            onClick={handleReset}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <X className="w-4 h-4" />
            Cancel
          </button>
        </div>
      )}

      {/* Security tip */}
      <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50">
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-slate-700 dark:text-slate-300 text-sm">
              Security Tips
            </p>
            <ul className="mt-1 space-y-1 text-xs text-slate-500 dark:text-slate-400">
              <li>• Use a unique password for each account</li>
              <li>• Consider using a password manager</li>
              <li>• Never share your password with others</li>
              <li>• Enable two-factor authentication if available</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PasswordTab
